import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router';
import { login } from '../api';
import { APP_NAME } from '../app/brand';
import { useAuthContext } from './context';
import controls from './controls.module.css';
import styles from './LoginPage.module.css';
import { describeLoginError, MISSING_CREDENTIALS, SESSION_EXPIRED } from './messages';
import { safeRedirectTarget } from './redirect';
import { LoadingScreen, SessionErrorScreen } from './StatusScreens';

type FocusTarget = 'email' | 'password' | 'submit';

/** O destino guardado por `RequireAuth` no `state` da navegação (pode ter sido forjado). */
function readFrom(state: unknown): unknown {
  return typeof state === 'object' && state !== null && 'from' in state ? state.from : undefined;
}

/** Página da rota /login. */
export function LoginPage() {
  const { state } = useAuthContext();
  const location = useLocation();

  switch (state.status) {
    case 'loading':
      return <LoadingScreen />;
    case 'error':
      // Sem saber se já há sessão, mostrar o formulário seria afirmar que a pessoa está deslogada.
      return <SessionErrorScreen error={state.error} onRetry={state.retry} />;
    case 'authenticated':
      // Cobre tanto quem abre /login já conectado quanto o fim de um login bem-sucedido.
      return <Navigate to={safeRedirectTarget(readFrom(location.state))} replace />;
    case 'unauthenticated':
      return <LoginForm expired={state.expired} />;
  }
}

function LoginForm({ expired }: { expired: boolean }) {
  const { signedIn } = useAuthContext();
  const baseId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  // Pedido de foco (e não `.focus()` direto): depois de um erro o botão ainda está desabilitado
  // até o próximo render, e um botão desabilitado não recebe foco.
  const [focusRequest, setFocusRequest] = useState<{ target: FocusTarget; seq: number } | null>({
    target: 'email',
    seq: 0,
  });
  // Guarda síncrona: `submitting` só vale depois do re-render, e Enter repetido pode chegar antes.
  const submittingRef = useRef(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = `Entrar · ${APP_NAME}`;
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (!focusRequest) return;
    const refs = { email: emailRef, password: passwordRef, submit: submitRef };
    refs[focusRequest.target].current?.focus();
  }, [focusRequest]);

  const requestFocus = (target: FocusTarget): void =>
    setFocusRequest((prev) => ({ target, seq: (prev?.seq ?? 0) + 1 }));

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submittingRef.current) return;

    // O campo type="email" já descarta espaços nas pontas; o trim cobre navegadores que não o fazem.
    const trimmedEmail = email.trim();
    const missingNow = { email: trimmedEmail === '', password: password === '' };
    setMissing(missingNow);
    if (missingNow.email || missingNow.password) {
      setError(MISSING_CREDENTIALS);
      requestFocus(missingNow.email ? 'email' : 'password');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const session = await login(trimmedEmail, password);
      // Segue "enviando" de propósito: a página troca assim que a sessão chega ao cache
      // (`LoginPage` vira o redirecionamento) e o formulário não deve reabrir no meio disso.
      signedIn(session);
    } catch (err) {
      const failure = describeLoginError(err);
      setError(failure.message);
      if (failure.credentialsRefused) setPassword('');
      requestFocus(failure.credentialsRefused ? 'password' : 'submit');
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const titleId = `${baseId}-title`;
  const emailId = `${baseId}-email`;
  const passwordId = `${baseId}-password`;
  const errorId = `${baseId}-error`;
  const describedBy = error ? errorId : undefined;

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <p className={styles.brand}>{APP_NAME}</p>
        <h1 id={titleId} className={styles.title}>
          Entrar
        </h1>
        {expired && (
          <p role="status" className={styles.notice}>
            {SESSION_EXPIRED}
          </p>
        )}
        <form
          className={styles.form}
          aria-labelledby={titleId}
          noValidate
          onSubmit={(event) => void handleSubmit(event)}
        >
          {error && (
            <p id={errorId} role="alert" className={styles.error}>
              {error}
            </p>
          )}
          <div className={styles.field}>
            <label htmlFor={emailId} className={styles.label}>
              E-mail
            </label>
            <input
              id={emailId}
              ref={emailRef}
              className={styles.input}
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={missing.email || undefined}
              aria-describedby={describedBy}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={passwordId} className={styles.label}>
              Senha
            </label>
            <input
              id={passwordId}
              ref={passwordRef}
              className={styles.input}
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={missing.password || undefined}
              aria-describedby={describedBy}
            />
          </div>
          <button
            type="submit"
            ref={submitRef}
            className={`${controls.primary} ${styles.submit}`}
            disabled={submitting}
          >
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
      </div>
    </main>
  );
}
